import React, { useRef, useCallback, useMemo } from 'react';
import { hslToRgb, toHex } from './colorMath';
import { hslToPolar, polarToHsl, normHue } from './harmony';

/* ─────────────────────────────────────────────────────────────
   The harmony wheel.

   Hue is the angle, saturation the radius. The disc is pure CSS —
   a conic-gradient for hue under a white radial-gradient for
   saturation — so there is no canvas to keep in sync and the whole
   thing scales with its container.

   Dragging any handle does not move that handle directly: it
   reports the hue and saturation under the pointer and lets the
   caller reseed the whole set from it. The geometry is therefore
   preserved by construction rather than by constraint checks, and
   the set cannot be dragged into a state the rule does not
   describe. Under the Custom rule the caller moves only the one
   handle instead.
─────────────────────────────────────────────────────────────── */

const SIZE = 210;          // disc diameter in px
const R = SIZE / 2;

const toXY = ({ h, s }) => {
  const { angle, radius } = hslToPolar({ h, s });
  const rad = (angle * Math.PI) / 180;
  return { x: R + Math.cos(rad) * radius * R, y: R + Math.sin(rad) * radius * R };
};

const HarmonyWheel = ({ colors, activeIndex = 0, onHandleMove, onHandlePick }) => {
  const discRef = useRef(null);
  const draggingRef = useRef(null);

  const points = useMemo(() => colors.map(toXY), [colors]);

  const report = useCallback((e, index) => {
    const el = discRef.current;
    if (!el) return;
    const box = el.getBoundingClientRect();
    /* Work in the disc's own coordinate space so the maths holds
       whatever the rendered size turns out to be. */
    const scale = box.width / SIZE || 1;
    const x = (e.clientX - box.left) / scale - R;
    const y = (e.clientY - box.top) / scale - R;
    const { h, s } = polarToHsl(x, y, R);
    onHandleMove?.(index, { h, s });
  }, [onHandleMove]);

  const onPointerDown = (e, index) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    draggingRef.current = index;
    onHandlePick?.(index);
    report(e, index);
  };

  const onPointerMove = e => {
    if (draggingRef.current === null) return;
    report(e, draggingRef.current);
  };

  const endDrag = e => {
    draggingRef.current = null;
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };

  const nudge = (e, index) => {
    const step = e.shiftKey ? 10 : 2;
    const dh = e.key === 'ArrowRight' ? step : e.key === 'ArrowLeft' ? -step : 0;
    const ds = e.key === 'ArrowUp' ? 0.02 : e.key === 'ArrowDown' ? -0.02 : 0;
    if (!dh && !ds) return;
    e.preventDefault();
    const current = colors[index];
    onHandleMove?.(index, {
      h: normHue(current.h + dh),
      s: Math.min(1, Math.max(0, current.s + ds)),
    });
  };

  return (
    <div
      className="stui-ch-wheel"
      ref={discRef}
      style={{ width: SIZE, height: SIZE }}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      {/* Connector lines sit under the handles and are decorative only. */}
      <svg className="stui-ch-links" viewBox={`0 0 ${SIZE} ${SIZE}`} aria-hidden="true">
        {points.map((p, i) => (
          <line key={i} x1={R} y1={R} x2={p.x} y2={p.y} />
        ))}
        {points.length > 2 && (
          <polygon points={points.map(p => `${p.x},${p.y}`).join(' ')} />
        )}
      </svg>

      {colors.map((c, i) => (
        <button
          key={i}
          type="button"
          className={`stui-ch-handle${i === 0 ? ' base' : ''}${i === activeIndex ? ' active' : ''}`}
          style={{
            left: points[i].x,
            top: points[i].y,
            backgroundColor: toHex(hslToRgb(c)),
          }}
          title={`${i === 0 ? 'Base — ' : ''}${toHex(hslToRgb(c))}`}
          aria-label={`Color ${i + 1}${i === 0 ? ', base' : ''}: hue ${Math.round(c.h)}, saturation ${Math.round(c.s * 100)}%`}
          onPointerDown={e => onPointerDown(e, i)}
          onKeyDown={e => nudge(e, i)}
        />
      ))}
    </div>
  );
};

export default HarmonyWheel;
